// Import React strict mode
import { StrictMode } from "react"
// Import React root creator
import { createRoot } from "react-dom/client"
// Import global CSS
import "./index.css"
// Import React Router functions
import { createBrowserRouter, RouterProvider } from "react-router-dom"
// Import Redux provider
import { Provider } from "react-redux"

// Import Redux store
import store from "./store/store.js"

// Import always-needed global UI components
import GlobalAlert from "./components/ui/GlobalAlert.jsx"
import GlobalLoader from "./components/loaders/GlobalLoader.jsx"
import LogoLoader from "./components/loaders/LogoLoader.jsx"
import SigninPage from "./pages/SigninPage.jsx"
import SignupPage from "./pages/SignupPage.jsx"
// Create browser router with lazy route loading
const router = createBrowserRouter([
  {
    // Root route path
    path: "/",
    // Lazily load root layouts
    async lazy() {
      // Import required layout module
      const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
      // Import required layout module
      const { default: MainLayout } = await import("./layouts/MainLayout.jsx")

      // Return route element
      return {
        element: (
          // Wrap main layout with auth layout
          <AuthLayout authentication={false}>
            <MainLayout />
          </AuthLayout>
        ),
      }
    },
    // Child routes
    children: [
      {
        // Home route
        path: "/",
        // Lazy load home page
        async lazy() {
          // Import required layout module
          const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
          // Import page component
          const { default: HomePage } = await import("./pages/HomePage.jsx")

          // Return route element
          return {
            element: (
              // Wrap page in auth layout
              <AuthLayout authentication={false}>
                <HomePage />
              </AuthLayout>
            ),
          }
        },
      },
      {
        path: "/signin",
        async lazy() {
          const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
          const { default: SigninPage } = await import("./pages/SigninPage.jsx")

          return {
            element: (
              <AuthLayout authentication={false}>
                <SigninPage />
              </AuthLayout>
            ),
          }
        },
      },
      {
        path: "/signup",
        async lazy() {
          const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
          const { default: SignupPage } = await import("./pages/SignupPage.jsx")

          return {
            element: (
              <AuthLayout authentication={false}>
                <SignupPage />
              </AuthLayout>
            ),
          }
        },
      },
      {
        // Alternate home route
        path: "/collections/home",
        // Lazy load home page
        async lazy() {
          // Import required layout module
          const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
          // Import page component
          const { default: HomePage } = await import("./pages/HomePage.jsx")

          // Return route element
          return {
            element: (
              // Wrap page in auth layout
              <AuthLayout authentication={false}>
                <HomePage />
              </AuthLayout>
            ),
          }
        },
      },
      {
        // Collection route
        path: "/collections/:collectionSlug",
        // Lazy load collection page
        async lazy() {
          // Import required layout module
          const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
          // Import page component
          const { default: CollectionPage } = await import("./pages/CollectionPage.jsx")

          // Return route element
          return {
            element: (
              // Wrap page in auth layout
              <AuthLayout authentication={false}>
                <CollectionPage />
              </AuthLayout>
            ),
          }
        },
      },
      {
        // Subcollection route
        path: "/collections/:collectionSlug/:subcollectionSlug",
        // Lazy load collection page
        async lazy() {
          // Import required layout module
          const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
          // Import page component
          const { default: CollectionPage } = await import("./pages/CollectionPage.jsx")

          // Return route element
          return {
            element: (
              // Wrap page in auth layout
              <AuthLayout authentication={false}>
                <CollectionPage />
              </AuthLayout>
            ),
          }
        },
      },
      {
        // Wishlist route
        path: "/wishlist",
        // Lazy load wishlist page
        async lazy() {
          // Import required layout module
          const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
          // Import page component
          const { default: WishlistPage } = await import("./pages/WishlistPage.jsx")

          // Return route element
          return {
            element: (
              // Wrap page in auth layout
              <AuthLayout authentication={true}>
                <WishlistPage />
              </AuthLayout>
            ),
          }
        },
      },
      {
        // Search route
        path: "/search",
        // Lazy load search page
        async lazy() {
          // Import required layout module
          const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
          // Import page component
          const { default: SearchPage } = await import("./pages/SearchPage.jsx")

          // Return route element
          return {
            element: (
              // Wrap page in auth layout
              <AuthLayout authentication={false}>
                <SearchPage />
              </AuthLayout>
            ),
          }
        },
      },
      {
        // Category route
        path: "/categories/:categorySlug",
        // Lazy load category page
        async lazy() {
          // Import required layout module
          const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
          // Import page component
          const { default: CategoryPage } = await import("./pages/CategoryPage.jsx")

          // Return route element
          return {
            element: (
              // Wrap page in auth layout
              <AuthLayout authentication={false}>
                <CategoryPage />
              </AuthLayout>
            ),
          }
        },
      },
      {
        // Product route
        path: "/products/:productSlug",
        // Lazy load product page
        async lazy() {
          // Import required layout module
          const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
          // Import page component
          const { default: ProductPage } = await import("./pages/ProductPage.jsx")

          // Return route element
          return {
            element: (
              // Wrap page in auth layout
              <AuthLayout authentication={false}>
                <ProductPage />
              </AuthLayout>
            ),
          }
        },
      },
      {
        // Account parent route
        path: "/account",
        // Lazy load account layout
        async lazy() {
          // Import required layout module
          const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
          // Import account layout
          const { default: AccountLayout } = await import("./layouts/AccountLayout.jsx")

          // Return route element
          return {
            element: (
              // Wrap page in auth layout
              <AuthLayout authentication={true}>
                <AccountLayout />
              </AuthLayout>
            ),
          }
        },
        // Account child routes
        children: [
          {
            // Default account page
            index: true,
            // Lazy load personal information component
            async lazy() {
              // Import component
              const { default: PersonalInformation } = await import("./components/account/PersonalInformation.jsx")

              // Return component for route
              return {
                Component: PersonalInformation,
              }
            },
          },
          {
            // Address page route
            path: "address",
            // Lazy load addresses component
            async lazy() {
              // Import component
              const { default: Addresses } = await import("./components/account/Addresses.jsx")

              // Return component for route
              return {
                Component: Addresses,
              }
            },
          },
          {
            // Orders page route
            path: "orders",
            // Lazy load orders component
            async lazy() {
              // Import component
              const { default: Orders } = await import("./components/account/Orders.jsx")

              // Return component for route
              return {
                Component: Orders,
              }
            },
          },
          {
            // Single order details route
            path: "orders/:orderId",
            // Lazy load account order details component
            async lazy() {
              // Import component
              const { default: AccountOrderDetails } = await import("./components/account/AccountOrderDetails.jsx")

              // Return component for route
              return {
                Component: AccountOrderDetails,
              }
            },
          },
          {
            // Password route
            path: "password",
            // Lazy load reset password component
            async lazy() {
              // Import component
              const { default: ResetPassword } = await import("./components/account/ResetPassword.jsx")

              // Return component for route
              return {
                Component: ResetPassword,
              }
            },
          },
        ],
      },
    ],
  },
  {
    // Checkout layout route
    path: "/checkout",
    // Lazy load checkout layout
    async lazy() {
      // Import auth layout
      const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
      // Import checkout layout
      const { default: CheckoutLayout } = await import("./layouts/CheckoutLayout.jsx")

      // Return route element
      return {
        element: (
          // Wrap checkout layout in auth layout
          <AuthLayout authentication={true}>
            <CheckoutLayout />
          </AuthLayout>
        ),
      }
    },
    // Checkout nested route
    children: [
      {
        // Default checkout route
        path: "",
        // Lazy load checkout page
        async lazy() {
          // Import auth layout
          const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
          // Import checkout page
          const { default: CheckoutPage } = await import("./pages/CheckoutPage.jsx")

          // Return route element
          return {
            element: (
              // Wrap page in auth layout
              <AuthLayout authentication={true}>
                <CheckoutPage />
              </AuthLayout>
            ),
          }
        },
      },
    ],
  },
  {
    // Order success route
    path: "/order-success/:orderId",
    // Lazy load order success page
    async lazy() {
      // Import auth layout
      const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
      // Import page component
      const { default: OrderSuccessPage } = await import("./pages/OrderSuccessPage.jsx")

      // Return route element
      return {
        element: (
          // Wrap page in auth layout
          <AuthLayout authentication={false}>
            <OrderSuccessPage />
          </AuthLayout>
        ),
      }
    },
  },
  {
    // Payment processing route
    path: "/payment-processing/:orderId",
    // Lazy load payment processing page
    async lazy() {
      // Import auth layout
      const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
      // Import page component
      const { default: PaymentProcessingPage } = await import("./pages/PaymentProcessingPage.jsx")

      // Return route element
      return {
        element: (
          // Wrap page in auth layout
          <AuthLayout authentication={false}>
            <PaymentProcessingPage />
          </AuthLayout>
        ),
      }
    },
  },
  {
    // Payment failed route
    path: "/payment-failed/:orderId",
    // Lazy load payment failed page
    async lazy() {
      // Import auth layout
      const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
      // Import page component
      const { default: PaymentFailedPage } = await import("./pages/PaymentFailedPage.jsx")

      // Return route element
      return {
        element: (
          // Wrap page in auth layout
          <AuthLayout authentication={false}>
            <PaymentFailedPage />
          </AuthLayout>
        ),
      }
    },
  },
  {
    // Payment success route
    path: "/payment-success/:orderId",
    // Lazy load payment success page
    async lazy() {
      // Import auth layout
      const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
      // Import page component
      const { default: PaymentSuccessPage } = await import("./pages/PaymentSuccess.jsx")

      // Return route element
      return {
        element: (
          // Wrap page in auth layout
          <AuthLayout authentication={false}>
            <PaymentSuccessPage />
          </AuthLayout>
        ),
      }
    },
  },
  {
    // Test route
    path: "/test",
    // Lazy load test page
    async lazy() {
      // Import auth layout
      const { default: AuthLayout } = await import("./layouts/AuthLayout.jsx")
      // Import page component
      const { default: TestPage } = await import("./pages/TestPage.jsx")

      // Return route element
      return {
        element: (
          // Wrap page in auth layout
          <AuthLayout authentication={false}>
            <TestPage />
          </AuthLayout>
        ),
      }
    },
  },
])

// Create React root and render application
createRoot(document.getElementById("root")).render(
  // Enable React strict mode checks
  <StrictMode>
    {/* Provide Redux store to whole app */}
    <Provider store={store}>
      {/* Render global alert */}
      <GlobalAlert />
      {/* Render global loader */}
      <GlobalLoader />
      {/* Render router with fallback while lazy routes load */}
      <RouterProvider
        router={router}
        fallbackElement={<LogoLoader message="Preparing Madam Ladies Wear..." />}
      />
    </Provider>
  </StrictMode>
)